import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles, tokens, Button } from '@fluentui/react-components';
import { iconMap } from '../theme/icons';
import { Card, Heading } from '../theme/primitives';
import { stonePalette } from '../theme/brand';

const CheckIcon = iconMap.check;
const CopyIcon = iconMap.content_copy;
const MailIcon = iconMap.mail;
const ProductionIcon = iconMap.precision_manufacturing;
const DeliveryIcon = iconMap.local_shipping;

const NEXT_STEPS = [
  { icon: MailIcon, key: 'step_email' },
  { icon: ProductionIcon, key: 'step_production' },
  { icon: DeliveryIcon, key: 'step_delivery' },
];

const useStyles = makeStyles({
  page: {
    minHeight: '80vh', backgroundColor: stonePalette[50],
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    paddingLeft: tokens.spacingHorizontalXL, paddingRight: tokens.spacingHorizontalXL,
  },
  inner: {
    maxWidth: '28rem', width: '100%', textAlign: 'center',
    animationDuration: tokens.durationSlower, animationTimingFunction: tokens.curveDecelerateMid,
    animationName: { from: { opacity: 0, transform: 'scale(0.96)' }, to: { opacity: 1, transform: 'scale(1)' } },
  },
  // Prekės ženklo spalvos švytėjimas po varnele — grynai dekoratyvus, neturi token atitikmens.
  iconCircle: {
    width: '96px', height: '96px', borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandBackground, color: tokens.colorNeutralForegroundOnBrand,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginLeft: 'auto', marginRight: 'auto', marginBottom: tokens.spacingVerticalXXL,
    boxShadow: '0 8px 32px rgba(9,170,137,0.4)',
  },
  title: { marginBottom: tokens.spacingVerticalS },
  subtitle: { marginBottom: tokens.spacingVerticalXXL },
  card: { marginBottom: tokens.spacingVerticalXXL, textAlign: 'left' },
  cardLabel: {
    fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: tokens.spacingVerticalS,
  },
  orderRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  orderNumber: {
    fontFamily: tokens.fontFamilyMonospace, fontWeight: tokens.fontWeightBold, fontSize: tokens.fontSizeBase500,
    color: tokens.colorNeutralForeground1, overflowWrap: 'anywhere',
  },
  copyBtn: {
    flexShrink: 0,
  },
  saveNote: { marginTop: tokens.spacingVerticalS, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  stepsList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  stepRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  stepIconChip: {
    width: '32px', height: '32px', borderRadius: tokens.borderRadiusMedium, flexShrink: 0,
    backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px',
  },
  stepText: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2 },
  actionsRow: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingHorizontalS,
    '@media (min-width: 640px)': { flexDirection: 'row' },
  },
  btnFlex: { flex: 1 },
});

const Success = () => {
  const { orderNumber } = useParams();
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();
  const s = useStyles();

  useEffect(() => { document.title = `${t('success_page.title')} | Fitit`; }, [t]);

  const copyOrderNumber = () => {
    navigator.clipboard.writeText(orderNumber).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <div className={s.page}>
      <div className={s.inner}>

        <div className={s.iconCircle}>
          <CheckIcon fontSize={44} />
        </div>

        <Heading variant="h1" className={s.title}>{t('success_page.title')}</Heading>
        <Heading variant="bodyLarge" className={s.subtitle}>{t('success_page.subtitle')}</Heading>

        {/* Order number */}
        <Card className={s.card} padding={tokens.spacingHorizontalL}>
          <div className={s.cardLabel}>{t('success_page.order_number')}</div>
          <div className={s.orderRow}>
            <div className={s.orderNumber}>{orderNumber}</div>
            <Button
              size="small"
              appearance={copied ? 'primary' : 'outline'}
              onClick={copyOrderNumber}
              icon={copied ? <CheckIcon fontSize={14} /> : <CopyIcon fontSize={14} />}
              className={s.copyBtn}
            >
              {copied ? t('success_page.copied_btn') : t('success_page.copy_btn')}
            </Button>
          </div>
          <div className={s.saveNote}>{t('success_page.save_note')}</div>
        </Card>

        {/* Next steps */}
        <Card className={s.card} padding={tokens.spacingHorizontalL}>
          <div className={s.cardLabel}>{t('success_page.next_steps')}</div>
          <div className={s.stepsList}>
            {NEXT_STEPS.map(({ icon: Icon, key }) => (
              <div key={key} className={s.stepRow}>
                <div className={s.stepIconChip}>
                  <Icon fontSize={16} />
                </div>
                <span className={s.stepText}>{t(`success_page.${key}`)}</span>
              </div>
            ))}
          </div>
        </Card>

        <div className={s.actionsRow}>
          <Button appearance="primary" size="large" as={Link} to="/" className={s.btnFlex}>{t('success_page.back_home')}</Button>
          <Button appearance="outline" size="large" as={Link} to="/konfiguratorius" className={s.btnFlex}>{t('success_page.create_another')}</Button>
        </div>
      </div>
    </div>
  );
};

export default Success;
